import React, { useState, useEffect, useRef } from 'react';
import { COUNTRIES_LIST, DEFAULT_COUNTRY } from '../../constants/countries';
import { usersService } from '../../services/db';
import { CameraIcon } from '../common/Icons';

/**
 * EditProfileModal
 * Allows contestants to update their Display Name, Avatar, Country, Phone Number, and Timezone.
 * Matches Flutter mobile EditProfileScreen and CompleteProfileScreen specifications.
 */
const EditProfileModal = ({ isOpen, onClose, profile, onProfileUpdated }) => {
  const [userName, setUserName] = useState('');
  const [selectedCountryName, setSelectedCountryName] = useState('United States');
  const [countryCode, setCountryCode] = useState('+1');
  const [phone, setPhone] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [availableTimezones, setAvailableTimezones] = useState([]);

  // Avatar state
  const [avatarPreview, setAvatarPreview] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const fileInputRef = useRef(null);

  // Submission & validation state
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Initialize fields whenever modal opens or profile changes
  useEffect(() => {
    if (profile) {
      setUserName(profile.userName || '');
      setPhone(profile.phone || '');
      setAvatarPreview(profile.img || '');
      setAvatarFile(null);
      setErrorMessage('');
      setSuccessMessage('');

      // Find matching country
      const foundCountry = COUNTRIES_LIST.find(
        (c) => c.name.toLowerCase() === (profile.country || '').toLowerCase()
      ) || DEFAULT_COUNTRY;

      setSelectedCountryName(foundCountry.name);
      setCountryCode(profile.countryCode || foundCountry.code || '+1');

      const tzones = foundCountry.timezones && foundCountry.timezones.length > 0
        ? foundCountry.timezones
        : ['UTC'];
      setAvailableTimezones(tzones);

      if (profile.timezone && tzones.includes(profile.timezone)) {
        setTimezone(profile.timezone);
      } else {
        setTimezone(tzones[0] || 'UTC');
      }
    }
  }, [profile, isOpen]);

  // When selected country changes, update dial code and timezone list
  const handleCountryChange = (e) => {
    const newCountryName = e.target.value;
    setSelectedCountryName(newCountryName);

    const countryObj = COUNTRIES_LIST.find((c) => c.name === newCountryName);
    if (countryObj) {
      setCountryCode(countryObj.code);
      const tzones = countryObj.timezones && countryObj.timezones.length > 0
        ? countryObj.timezones
        : ['UTC'];
      setAvailableTimezones(tzones);
      setTimezone(tzones[0] || 'UTC');
    }
  };

  // Image Selection Handler
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrorMessage('Please select a valid image file (JPG, PNG, WebP).');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage('Image size must be less than 5MB.');
      return;
    }

    setErrorMessage('');
    setAvatarFile(file);

    // Live preview
    const reader = new FileReader();
    reader.onload = () => {
      setAvatarPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!userName.trim()) {
      setErrorMessage('Display Name cannot be empty.');
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      let finalImgUrl = profile?.img || '';

      // Upload image to Firebase Storage if a new file was chosen
      if (avatarFile && profile?.userId) {
        finalImgUrl = await usersService.uploadProfileImage(profile.userId, avatarFile);
      }

      // Update Firestore document
      const updateData = {
        userName: userName.trim(),
        country: selectedCountryName,
        countryCode: countryCode,
        phone: phone.trim(),
        timezone: timezone,
        img: finalImgUrl,
      };

      await usersService.updateUserProfile(profile.userId, updateData);

      setSuccessMessage('Profile updated successfully!');
      if (onProfileUpdated) {
        onProfileUpdated({ ...profile, ...updateData, img: finalImgUrl });
      }

      setTimeout(() => {
        onClose();
      }, 700);
    } catch (err) {
      console.error('Failed to update profile:', err);
      setErrorMessage(err.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="profile-modal-overlay" onClick={onClose}>
      <div
        className="profile-modal-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-profile-modal-title"
      >
        {/* Header */}
        <div className="profile-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div className="profile-modal-icon-badge">
              <span>👤</span>
            </div>
            <div>
              <h2 id="edit-profile-modal-title" className="profile-modal-title">
                Edit Profile
              </h2>
              <p className="profile-modal-subtitle">
                Update your display name, country, and contact details
              </p>
            </div>
          </div>
          <button
            type="button"
            className="profile-modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSave} className="profile-modal-form">
          {/* Avatar Photo Section */}
          <div className="profile-avatar-edit-wrap">
            <div className="profile-avatar-preview-box">
              {avatarPreview ? (
                <img
                  src={avatarPreview}
                  alt={userName || 'Avatar'}
                  className="profile-avatar-img"
                  onError={() => setAvatarPreview('')}
                />
              ) : (
                <div className="profile-avatar-placeholder">
                  {(userName || 'P').slice(0, 2).toUpperCase()}
                </div>
              )}
              <button
                type="button"
                className="profile-avatar-upload-trigger"
                onClick={() => fileInputRef.current?.click()}
                title="Change Photo"
                aria-label="Change Profile Photo"
              >
                <CameraIcon size={13} style={{ color: 'var(--clr-primary, #FF5C56)' }} />
              </button>
            </div>
            <div className="profile-avatar-info">
              <div style={{ fontWeight: 600, fontSize: '13.5px', color: 'var(--clr-text-1)' }}>
                Profile Picture
              </div>
              <div style={{ fontSize: '12px', color: 'var(--clr-text-muted)' }}>
                JPG, PNG or WebP. Max 5MB.
              </div>
              <button
                type="button"
                className="profile-avatar-change-btn"
                onClick={() => fileInputRef.current?.click()}
              >
                Upload New Photo
              </button>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              style={{ display: 'none' }}
            />
          </div>

          {/* Feedback messages */}
          {errorMessage && (
            <div className="profile-alert profile-alert--error">
              <span>⚠️</span>
              <span>{errorMessage}</span>
            </div>
          )}

          {successMessage && (
            <div className="profile-alert profile-alert--success">
              <span>✓</span>
              <span>{successMessage}</span>
            </div>
          )}

          {/* Display Name */}
          <div className="profile-form-group">
            <label className="profile-form-label" htmlFor="edit-profile-username">
              Display Name <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <input
              id="edit-profile-username"
              type="text"
              className="profile-form-input"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="e.g. David Solomon"
              maxLength={50}
              required
            />
            <span className="profile-form-hint">
              This name will appear on global leaderboards and game results.
            </span>
          </div>

          {/* Country Selection */}
          <div className="profile-form-group">
            <label className="profile-form-label" htmlFor="edit-profile-country">
              Country / Region
            </label>
            <select
              id="edit-profile-country"
              className="profile-form-select"
              value={selectedCountryName}
              onChange={handleCountryChange}
            >
              {COUNTRIES_LIST.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name} ({c.code})
                </option>
              ))}
            </select>
          </div>

          {/* Phone Number with Country Dial Code Prefix */}
          <div className="profile-form-group">
            <label className="profile-form-label" htmlFor="edit-profile-phone">
              Phone Number <span style={{ fontSize: '11px', color: 'var(--clr-text-muted)' }}>(Private, only visible to you)</span>
            </label>
            <div className="profile-phone-input-wrap">
              <span className="profile-phone-prefix">{countryCode}</span>
              <input
                id="edit-profile-phone"
                type="tel"
                className="profile-form-input profile-phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/[^0-9\s-]/g, ''))}
                placeholder="e.g. 555-0199"
                maxLength={20}
              />
            </div>
          </div>

          {/* Timezone */}
          <div className="profile-form-group">
            <label className="profile-form-label" htmlFor="edit-profile-timezone">
              Timezone
            </label>
            <select
              id="edit-profile-timezone"
              className="profile-form-select"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            >
              {availableTimezones.map((tz) => (
                <option key={tz} value={tz}>
                  {tz.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
            <span className="profile-form-hint">
              Used to calculate tournament start times and devotion streaks in your local time.
            </span>
          </div>

          {/* Action Buttons */}
          <div className="profile-modal-actions">
            <button
              type="button"
              className="profile-modal-btn profile-modal-btn--secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="profile-modal-btn profile-modal-btn--primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="profile-btn-spinner" />
                  <span>Saving...</span>
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfileModal;
